import { useMemo } from 'react';
import { useProviderQuickSetupGuide } from '../../hooks/useIntegrations';
import { Card, CardHeader, MarkdownContent } from '@/components/ui';
import { Sparkles } from 'lucide-react';
import type { Integration } from '../../types/integrations';
import {
  applyGuideTokens,
  extractCopyMarkers,
  getResolvedTokenValues,
} from '../../lib/integration-guide-tokens';

interface QuickSetupPanelProps {
  provider: string;
  providerDisplayName: string;
  documentationUrl?: string;
  /** Available post-creation. Pre-submission renders leave URL tokens literal. */
  integration?: Integration;
}

/**
 * Prominent, opinionated walkthrough rendered above the long-form setup
 * guide. Backend resolves to null for providers without a quick guide; we
 * render nothing in that case so non-Slack providers see no "empty card".
 */
export default function QuickSetupPanel({
  provider,
  providerDisplayName,
  documentationUrl,
  integration,
}: QuickSetupPanelProps) {
  const { data: markdown, isLoading } = useProviderQuickSetupGuide(provider);

  const guideContext = useMemo(
    () => ({
      integration,
      provider: { name: provider, displayName: providerDisplayName, documentationUrl },
    }),
    [integration, provider, providerDisplayName, documentationUrl],
  );

  const { resolvedMarkdown, copyableValues } = useMemo(() => {
    if (!markdown) {
      return { resolvedMarkdown: markdown, copyableValues: [] as string[] };
    }
    const { markdown: demarked, values: markedCopyables } = extractCopyMarkers(markdown);
    return {
      resolvedMarkdown: applyGuideTokens(demarked, guideContext),
      copyableValues: [
        ...getResolvedTokenValues(guideContext),
        ...markedCopyables.map((v) => applyGuideTokens(v, guideContext)),
      ],
    };
  }, [markdown, guideContext]);

  if (isLoading) {
    return (
      <Card className="ring-1 ring-primary-200 dark:ring-primary-900/40">
        <div className="space-y-2">
          <div className="h-3 bg-muted rounded animate-pulse w-3/4" />
          <div className="h-3 bg-muted rounded animate-pulse w-full" />
          <div className="h-3 bg-muted rounded animate-pulse w-5/6" />
        </div>
      </Card>
    );
  }

  if (!resolvedMarkdown) return null;

  return (
    <Card className="ring-1 ring-primary-200 dark:ring-primary-900/40">
      <CardHeader
        title="Quick setup"
        description={`Get your ${providerDisplayName} integration up and running.`}
        action={
          <Sparkles className="h-5 w-5 text-primary-500" aria-hidden="true" />
        }
      />
      <MarkdownContent size="sm" copyableValues={copyableValues}>
        {resolvedMarkdown}
      </MarkdownContent>
    </Card>
  );
}
